import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import NoSearchResult from "../../../components/Common/NoSearchResult";
import { useMutation } from "@tanstack/react-query";
import { TrafficOrderService } from "../../../services/traffic_order.service";
import { useDispatch, useSelector } from "react-redux";
import { getBrokersId, getCountries, getTeamsAll, getUsersAll } from "src/store/actions";
import useOffices from "src/hooks/useOffices";
import { formatDate } from "../../../helpers/formatDate";
import FiltersPanel from "./components/FiltersPanel";
import SummaryCards from "./components/SummaryCards";
import TimelineCard from "./components/TimelineCard";
import StatusCard from "./components/StatusCard";
import BarChartCard from "./components/BarChartCard";
import StatsTableCard from "./components/StatsTableCard";
import type {
  AffiliateStat,
  BrokerStat,
  BuyerTeamStat,
  CountryStat,
  OfficeStat,
  PayMethodStat,
  QuickRangeKey,
  SelectOption,
  StatusPoint,
  SummaryStats,
  TableColumn,
  TimelinePoint,
} from "./components/types";
import { getAuthUser } from "../../../helpers/auth";

const startOfWeek = (date: Date, weekStartsOn: number) => {
  const day = date.getDay();
  const diff = (day + 7 - weekStartsOn) % 7;
  const start = new Date(date);
  start.setDate(date.getDate() - diff);
  start.setHours(0, 0, 0, 0);
  return start;
};

const endOfWeek = (date: Date, weekStartsOn: number) => {
  const start = startOfWeek(date, weekStartsOn);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

const startOfMonth = (date: Date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  return start;
};

const endOfMonth = (date: Date) => {
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return end;
};

const startOfYear = (date: Date) => {
  const start = new Date(date.getFullYear(), 0, 1);
  start.setHours(0, 0, 0, 0);
  return start;
};

const endOfYear = (date: Date) => {
  const end = new Date(date.getFullYear(), 11, 31);
  end.setHours(23, 59, 59, 999);
  return end;
};

const StatisticOrder = () => {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [quickRange, setQuickRange] = useState<QuickRangeKey | null>(null);
  const [selectedOffices, setSelectedOffices] = useState<SelectOption[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<SelectOption[]>([]);
  const [selectedBrokers, setSelectedBrokers] = useState<SelectOption[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<SelectOption[]>([]);
  const [selectedPayMethods, setSelectedPayMethods] = useState<SelectOption[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<SelectOption[]>([]);
  const [selectedResponsibles, setSelectedResponsibles] = useState<SelectOption[]>([]);
  const { user_offices } = useOffices();
  const brokersRaw = useSelector((state: any) => state.Brokers?.dataBrokerId || []);
  const usersRaw = useSelector((state: any) => state.Users?.members || []);
  const teamsRaw = useSelector((state: any) => state.Teams?.members || []);
  const countriesRaw = useSelector((state: any) => state.Countries?.data || []);
  const authUser = getAuthUser();

  const { mutate, data: resultData, isError, isPending, isSuccess, error, reset } = useMutation({
    mutationFn: TrafficOrderService.fetchStatistic,
    mutationKey: ["keyFetchStatistic"],
  });

  useEffect(() => {
    dispatch(getBrokersId());
    dispatch(getUsersAll());
    dispatch(getTeamsAll());
    dispatch(getCountries());
  }, [dispatch]);

  const officeOptions = useMemo<SelectOption[]>(() => (
    user_offices.map((office: any) => ({
      value: office.id,
      label: office.name,
    }))
  ), [user_offices]);

  const brokerOptions = useMemo<SelectOption[]>(() => (
    Array.isArray(brokersRaw) ? brokersRaw.map((broker: any) => ({
      value: broker.value ?? broker.id,
      label: broker.label ?? broker.name,
      office_id: broker.office_id,
    })) : []
  ), [brokersRaw]);

  const isBuyerTeamLead = useMemo(() => (
    authUser?.roles?.some((role) => role.name === "buyer_teamlead") ?? false
  ), [authUser?.roles]);

  const teamOptions = useMemo<SelectOption[]>(() => {
    const raw = Array.isArray(teamsRaw) ? teamsRaw : [];
    const mapped = raw.map((team: any) => ({
      value: team.value ?? team.id,
      label: team.label ?? team.name,
    }));
    if (isBuyerTeamLead && authUser?.team_id) {
      return mapped.filter((team) => team.value === authUser.team_id);
    }
    return mapped;
  }, [teamsRaw, isBuyerTeamLead, authUser?.team_id]);

  const countryOptions = useMemo<SelectOption[]>(() => {
    const raw = Array.isArray(countriesRaw) ? countriesRaw : [];
    return raw.map((country: any) => ({
      value: country.id,
      label: country.iso ? `${country.title} (${country.iso})` : country.title,
    }));
  }, [countriesRaw]);

  const payMethodOptions = useMemo<SelectOption[]>(() => ([
    { value: 1, label: "CPA" },
    { value: 2, label: "CPL" },
    { value: 3, label: "CRG" },
  ]), []);

  const selectedOfficeIds = useMemo<number[]>(() => (
    selectedOffices.map((office) => office.value).filter(Boolean)
  ), [selectedOffices]);

  const selectedTeamIds = useMemo<number[]>(() => (
    selectedTeams.map((team) => team.value).filter(Boolean)
  ), [selectedTeams]);

  const filteredBrokerOptions = useMemo<SelectOption[]>(() => {
    if (!selectedOfficeIds.length) return brokerOptions;
    return brokerOptions.filter((broker) => selectedOfficeIds.includes(broker.office_id ?? 0));
  }, [brokerOptions, selectedOfficeIds]);

  const statusFilterOptions = useMemo<SelectOption[]>(() => ([
    { value: 1, label: "Active" },
    { value: 2, label: "Pause" },
    { value: 3, label: "Stop" },
    { value: 4, label: "Waiting" },
    { value: 5, label: "Not Active" },
  ]), []);

  const selectedBrokerIds = useMemo<number[]>(() => (
    selectedBrokers.map((broker) => broker.value).filter(Boolean)
  ), [selectedBrokers]);

  const selectedCountryIds = useMemo<number[]>(() => (
    selectedCountries.map((country) => country.value).filter(Boolean)
  ), [selectedCountries]);

  const selectedPayMethodIds = useMemo<number[]>(() => (
    selectedPayMethods.map((method) => method.value).filter(Boolean)
  ), [selectedPayMethods]);

  const selectedStatusIds = useMemo<number[]>(() => (
    selectedStatuses.map((status) => status.value).filter(Boolean)
  ), [selectedStatuses]);

  const selectedResponsibleIds = useMemo<number[]>(() => (
    selectedResponsibles.map((user) => user.value).filter(Boolean)
  ), [selectedResponsibles]);

  const hasFilters = Boolean(
    selectedOfficeIds.length ||
    selectedTeamIds.length ||
    selectedBrokerIds.length ||
    selectedCountryIds.length ||
    selectedPayMethodIds.length ||
    selectedStatusIds.length ||
    selectedResponsibleIds.length
  );

  const resetFilters = useCallback(() => {
    setSelectedOffices([]);
    setSelectedTeams([]);
    setSelectedBrokers([]);
    setSelectedCountries([]);
    setSelectedPayMethods([]);
    setSelectedStatuses([]);
    setSelectedResponsibles([]);
  }, []);

  const handleDateChange = useCallback((selectedDates: Date[]) => {
    if (selectedDates.length === 0) {
      setQuickRange(null);
      setDateRange([null, null]);
      return;
    }
    if (selectedDates.length === 2) {
      const [first, second] = selectedDates;
      const from = first <= second ? first : second;
      const to = first <= second ? second : first;
      setQuickRange(null);
      setDateRange([from, to]);
    }
  }, []);

  const applyQuickRange = useCallback((key: QuickRangeKey) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekStartsOn = 1;
    let start: Date | null = null;
    let end: Date | null = null;

    switch (key) {
      case "today":
        start = today;
        end = today;
        break;
      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        start = yesterday;
        end = yesterday;
        break;
      }
      case "this_week":
        start = startOfWeek(today, weekStartsOn);
        end = today;
        break;
      case "last_week": {
        const lastWeekRef = new Date(today);
        lastWeekRef.setDate(today.getDate() - 7);
        start = startOfWeek(lastWeekRef, weekStartsOn);
        end = endOfWeek(lastWeekRef, weekStartsOn);
        break;
      }
      case "this_month":
        start = startOfMonth(today);
        end = today;
        break;
      case "last_month": {
        const lastMonthRef = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        start = startOfMonth(lastMonthRef);
        end = endOfMonth(lastMonthRef);
        break;
      }
      case "this_year":
        start = startOfYear(today);
        end = today;
        break;
      case "last_year": {
        const lastYearRef = new Date(today.getFullYear() - 1, 0, 1);
        start = startOfYear(lastYearRef);
        end = endOfYear(lastYearRef);
        break;
      }
      default:
        break;
    }

    if (start && end) {
      setQuickRange(key);
      setDateRange([start, end]);
    }
  }, []);

  useEffect(() => {
    if (!selectedOfficeIds.length) return;
    setSelectedBrokers((prev) =>
      (prev || []).filter((broker) => selectedOfficeIds.includes(broker.office_id ?? 0))
    );
  }, [selectedOfficeIds]);

  const formatRangeDate = useCallback((range: [Date | null, Date | null]) => {
    const [fromDate, toDate] = range;

    return {
      fromDate: fromDate ? formatDate(fromDate) : null,
      toDate: toDate ? formatDate(toDate) : null,
    };
  }, []);

  useEffect(() => {
    const formattedDates = formatRangeDate(dateRange);
    if (formattedDates.fromDate && formattedDates.toDate) {
      const payload: any = {
        from_date: formattedDates.fromDate,
        to_date: formattedDates.toDate,
      };
      if (selectedOfficeIds.length) payload.office_ids = selectedOfficeIds;
      if (selectedTeamIds.length) payload.team_ids = selectedTeamIds;
      if (selectedBrokerIds.length) payload.broker_ids = selectedBrokerIds;
      if (selectedCountryIds.length) payload.country_ids = selectedCountryIds;
      if (selectedPayMethodIds.length) payload.pay_method_ids = selectedPayMethodIds;
      if (selectedStatusIds.length) payload.status = selectedStatusIds;
      if (selectedResponsibleIds.length) payload.responsible_user_ids = selectedResponsibleIds;
      mutate(payload);
    } else {
      reset();
    }
  }, [
    dateRange,
    selectedOfficeIds,
    selectedTeamIds,
    selectedBrokerIds,
    selectedCountryIds,
    selectedPayMethodIds,
    selectedStatusIds,
    selectedResponsibleIds,
    mutate,
    reset,
    formatRangeDate,
  ]);

  useLayoutEffect(() => {
    document.title = "Dashboard";

    applyQuickRange("today");
  }, []);

  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);
  const formatNumber = useCallback((value?: number | string) => {
    const numeric = typeof value === "number" ? value : Number(value ?? 0);
    return numberFormatter.format(Number.isFinite(numeric) ? numeric : 0);
  }, [numberFormatter]);

  const formatPercent = useCallback((value?: number | string) => {
    const numeric = typeof value === "number" ? value : Number(value ?? 0);
    const safe = Number.isFinite(numeric) ? numeric : 0;
    return `${safe.toFixed(2)}%`;
  }, []);

  const summary: SummaryStats | null = resultData?.data?.summary ?? null;
  const timeline: TimelinePoint[] = resultData?.data?.timeline ?? [];
  const status: StatusPoint[] = resultData?.data?.status ?? [];
  const funnel: any[] = resultData?.data?.funnel ?? [];
  const brokers: BrokerStat[] = resultData?.data?.brokers ?? [];
  const offices: OfficeStat[] = resultData?.data?.offices ?? [];
  const buyerTeams: BuyerTeamStat[] = resultData?.data?.buyer_teams ?? [];
  const affiliates: AffiliateStat[] = resultData?.data?.affiliates ?? [];
  const countries: CountryStat[] = resultData?.data?.countries ?? [];
  const payMethods: PayMethodStat[] = resultData?.data?.pay_methods ?? [];

  const funnelChart = useMemo(() => {
    if (!isSuccess || !Array.isArray(funnel)) return { categories: [], seriesData: [] };
    return {
      categories: funnel.map((item: any) => item.funnel_name),
      seriesData: funnel.map((item: any) => item.total_leads),
    };
  }, [isSuccess, funnel]);

  const brokersChart = useMemo(() => {
    if (!isSuccess || !Array.isArray(brokers)) return { categories: [], seriesData: [] };
    return {
      categories: brokers.map((item) => item.broker_name),
      seriesData: brokers.map((item) => item.total_leads),
    };
  }, [isSuccess, brokers]);

  const buyerTeamsChart = useMemo(() => {
    if (!isSuccess || !Array.isArray(buyerTeams)) return { categories: [], seriesData: [] };
    return {
      categories: buyerTeams.map((item) => item.team_name ?? "No team"),
      seriesData: buyerTeams.map((item) => item.total_leads),
    };
  }, [isSuccess, buyerTeams]);

  const affiliatesChart = useMemo(() => {
    if (!isSuccess || !Array.isArray(affiliates)) return { categories: [], seriesData: [] };
    return {
      categories: affiliates.map((item) => item.affiliate_name),
      seriesData: affiliates.map((item) => item.total_leads),
    };
  }, [isSuccess, affiliates]);

  const countriesChart = useMemo(() => {
    if (!isSuccess || !Array.isArray(countries)) return { categories: [], seriesData: [] };
    return {
      categories: countries.map((item) => item.country_title),
      seriesData: countries.map((item) => item.total_leads),
    };
  }, [isSuccess, countries]);

  const payMethodsChart = useMemo(() => {
    if (!isSuccess || !Array.isArray(payMethods)) return { categories: [], seriesData: [] };
    return {
      categories: payMethods.map((item) => item.pay_method_label),
      seriesData: payMethods.map((item) => item.total_leads),
    };
  }, [isSuccess, payMethods]);

  const timelineCategories = useMemo(() => timeline.map((item) => item.date), [timeline]);
  const timelineSeries = useMemo(() => ([
    {
      name: "Leads",
      type: "column",
      data: timeline.map((item) => item.total_leads),
    },
    {
      name: "Daily Cap",
      type: "line",
      data: timeline.map((item) => item.total_daily_cap),
    },
  ]), [timeline]);

  const timelineOptions = useMemo(() => ({
    chart: {
      toolbar: { show: false },
    },
    stroke: {
      width: [0, 3],
      curve: "smooth",
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["#556ee6", "#34c38f"],
    xaxis: {
      categories: timelineCategories,
    },
    yaxis: [
      {
        title: { text: "Leads" },
      },
      {
        opposite: true,
        title: { text: "Daily Cap" },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
    },
  }), [timelineCategories]);

  const statusLabels = useMemo(() => status.map((item) => item.label), [status]);
  const statusSeries = useMemo(() => status.map((item) => item.total_days), [status]);
  const statusChartOptions = useMemo(() => ({
    labels: statusLabels,
    legend: { position: "bottom" },
    colors: ["#34c38f", "#f1b44c", "#f46a6a", "#50a5f1", "#74788d"],
  }), [statusLabels]);

  const rangeLabel = useMemo(() => {
    if (!dateRange[0] || !dateRange[1]) return "No range selected";

    if (dateRange[0]?.getDate() === dateRange[1]?.getDate())
        return `${formatDate(dateRange[0])}`;

    return `${formatDate(dateRange[0])} - ${formatDate(dateRange[1])}`;
  }, [dateRange]);

  const hasRange = Boolean(dateRange[0] && dateRange[1]);
  const hasData = Boolean(summary?.total_orders && summary?.total_orders > 0);
  const isEmpty = hasRange && !isPending && !isError && !hasData;
  const errorMessage =
    (error as any)?.response?.data?.message ||
    (error as any)?.message ||
    "Failed to load statistic";

  const brokerColumns = useMemo<TableColumn<BrokerStat>[]>(() => ([
    { label: "#", render: (_row, index) => index + 1 },
    { label: "Broker", render: (row) => row.broker_name },
    { label: "Orders", align: "end", render: (row) => formatNumber(row.total_orders) },
    { label: "Leads", align: "end", render: (row) => formatNumber(row.total_leads) },
    { label: "Daily cap", align: "end", render: (row) => formatNumber(row.total_daily_cap) },
    { label: "Utilization", align: "end", render: (row) => formatPercent(row.utilization_pct) },
  ]), [formatNumber, formatPercent]);

  const officeColumns = useMemo<TableColumn<OfficeStat>[]>(() => ([
    { label: "#", render: (_row, index) => index + 1 },
    { label: "Office", render: (row) => row.office_name },
    { label: "Orders", align: "end", render: (row) => formatNumber(row.total_orders) },
    { label: "Leads", align: "end", render: (row) => formatNumber(row.total_leads) },
    { label: "Daily cap", align: "end", render: (row) => formatNumber(row.total_daily_cap) },
    { label: "Utilization", align: "end", render: (row) => formatPercent(row.utilization_pct) },
  ]), [formatNumber, formatPercent]);

  const buyerTeamColumns = useMemo<TableColumn<BuyerTeamStat>[]>(() => ([
    { label: "#", render: (_row, index) => index + 1 },
    { label: "Team", render: (row) => row.team_name ?? "No team" },
    { label: "Orders", align: "end", render: (row) => formatNumber(row.total_orders) },
    { label: "Leads", align: "end", render: (row) => formatNumber(row.total_leads) },
    { label: "Daily cap", align: "end", render: (row) => formatNumber(row.total_daily_cap) },
    { label: "Utilization", align: "end", render: (row) => formatPercent(row.utilization_pct) },
  ]), [formatNumber, formatPercent]);

  const affiliateColumns = useMemo<TableColumn<AffiliateStat>[]>(() => ([
    { label: "#", render: (_row, index) => index + 1 },
    { label: "Affiliate", render: (row) => row.affiliate_name },
    { label: "Orders", align: "end", render: (row) => formatNumber(row.total_orders) },
    { label: "Leads", align: "end", render: (row) => formatNumber(row.total_leads) },
    { label: "Daily cap", align: "end", render: (row) => formatNumber(row.total_daily_cap) },
    { label: "Utilization", align: "end", render: (row) => formatPercent(row.utilization_pct) },
  ]), [formatNumber, formatPercent]);

  const countryColumns = useMemo<TableColumn<CountryStat>[]>(() => ([
    { label: "#", render: (_row, index) => index + 1 },
    { label: "Country", render: (row) => row.country_title },
    { label: "Orders", align: "end", render: (row) => formatNumber(row.total_orders) },
    { label: "Leads", align: "end", render: (row) => formatNumber(row.total_leads) },
    { label: "Daily cap", align: "end", render: (row) => formatNumber(row.total_daily_cap) },
    { label: "Utilization", align: "end", render: (row) => formatPercent(row.utilization_pct) },
  ]), [formatNumber, formatPercent]);

  const payMethodColumns = useMemo<TableColumn<PayMethodStat>[]>(() => ([
    { label: "#", render: (_row, index) => index + 1 },
    { label: "Pay model", render: (row) => row.pay_method_label },
    { label: "Orders", align: "end", render: (row) => formatNumber(row.total_orders) },
    { label: "Leads", align: "end", render: (row) => formatNumber(row.total_leads) },
    { label: "Daily cap", align: "end", render: (row) => formatNumber(row.total_daily_cap) },
    { label: "Utilization", align: "end", render: (row) => formatPercent(row.utilization_pct) },
  ]), [formatNumber, formatPercent]);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Dashboard" breadcrumbItem="Statistic Traffic Orders" />

        <FiltersPanel
          filtersOpen={filtersOpen}
          rangeLabel={rangeLabel}
          dateRange={dateRange}
          onToggle={() => setFiltersOpen((prev) => !prev)}
          onDateChange={handleDateChange}
          onQuickRange={applyQuickRange}
          selectedQuickRange={quickRange}
          officeOptions={officeOptions}
          teamOptions={teamOptions}
          brokerOptions={filteredBrokerOptions}
          countryOptions={countryOptions}
          payMethodOptions={payMethodOptions}
          statusOptions={statusFilterOptions}
          userOptions={usersRaw}
          selectedOffices={selectedOffices}
          selectedTeams={selectedTeams}
          selectedBrokers={selectedBrokers}
          selectedCountries={selectedCountries}
          selectedPayMethods={selectedPayMethods}
          selectedStatuses={selectedStatuses}
          selectedResponsibles={selectedResponsibles}
          onOfficesChange={setSelectedOffices}
          onTeamsChange={setSelectedTeams}
          onBrokersChange={setSelectedBrokers}
          onCountriesChange={setSelectedCountries}
          onPayMethodsChange={setSelectedPayMethods}
          onStatusesChange={setSelectedStatuses}
          onResponsiblesChange={setSelectedResponsibles}
          onReset={resetFilters}
          hasFilters={hasFilters}
        />

        {isPending && (
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <NoSearchResult text="Loading..." />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {isError && (
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <NoSearchResult text={errorMessage} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {isEmpty && (
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <NoSearchResult text="No data for selected range" />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {!hasRange && !isPending && !isError && (
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <NoSearchResult text="Select a date range to build the report" />
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {isSuccess && hasData && (
          <>
            <SummaryCards
              summary={summary}
              formatNumber={formatNumber}
              formatPercent={formatPercent}
            />

            <Row className="mt-3">
              <Col xl={8}>
                <TimelineCard options={timelineOptions} series={timelineSeries} />
              </Col>
              <Col xl={4}>
                <StatusCard
                  statusSeries={statusSeries}
                  statusOptions={statusChartOptions}
                  rows={status}
                  formatNumber={formatNumber}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col xl={6}>
                <BarChartCard
                  title="Top funnels by leads"
                  categories={funnelChart.categories}
                  seriesData={funnelChart.seriesData}
                  seriesName="Leads"
                />
              </Col>
              <Col xl={6}>
                <BarChartCard
                  title="Top brokers by leads"
                  categories={brokersChart.categories}
                  seriesData={brokersChart.seriesData}
                  seriesName="Leads"
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col xl={6}>
                <StatsTableCard
                  title="Top brokers details"
                  rows={brokers}
                  columns={brokerColumns}
                />
              </Col>
              <Col xl={6}>
                <StatsTableCard
                  title="Top offices details"
                  rows={offices}
                  columns={officeColumns}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col xl={6}>
                <BarChartCard
                  title="Buyer teams by leads"
                  categories={buyerTeamsChart.categories}
                  seriesData={buyerTeamsChart.seriesData}
                  seriesName="Leads"
                />
              </Col>
              <Col xl={6}>
                <BarChartCard
                  title="Affiliates by leads"
                  categories={affiliatesChart.categories}
                  seriesData={affiliatesChart.seriesData}
                  seriesName="Leads"
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col xl={6}>
                <BarChartCard
                  title="Countries by leads"
                  categories={countriesChart.categories}
                  seriesData={countriesChart.seriesData}
                  seriesName="Leads"
                />
              </Col>
              <Col xl={6}>
                <BarChartCard
                  title="Pay models by leads"
                  categories={payMethodsChart.categories}
                  seriesData={payMethodsChart.seriesData}
                  seriesName="Leads"
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col xl={6}>
                <StatsTableCard
                  title="Buyer teams details"
                  rows={buyerTeams}
                  columns={buyerTeamColumns}
                />
              </Col>
              <Col xl={6}>
                <StatsTableCard
                  title="Affiliates details"
                  rows={affiliates}
                  columns={affiliateColumns}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col xl={6}>
                <StatsTableCard
                  title="Countries details"
                  rows={countries}
                  columns={countryColumns}
                />
              </Col>
              <Col xl={6}>
                <StatsTableCard
                  title="Pay models details"
                  rows={payMethods}
                  columns={payMethodColumns}
                />
              </Col>
            </Row>
          </>
        )}
      </Container>
    </div>
  );
};

export default StatisticOrder;
